import { Children, isValidElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Floating } from './Floating';

// ─────────────────────────────────────────────────────────────────────────────
// SelectField — the app-wide searchable dropdown.
//
// A drop-in replacement for a native <select>: same `value`, same
// `onChange={e => … e.target.value}`, same <option>/<optgroup> children and
// `className`. The panel is portalled through <Floating> (never clipped) and
// has a search box, keyboard navigation and a tick on the selected option.
// ─────────────────────────────────────────────────────────────────────────────

interface Opt { value: string; label: string; disabled?: boolean; group?: string }

type ChangeLike = { target: { value: string; name?: string }; currentTarget: { value: string; name?: string } };

interface Props {
  value?: string | number | null;
  defaultValue?: string | number;
  onChange?: (e: ChangeLike) => void;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  title?: string;
  /** Show the search box from this many options up. Default 6. */
  searchFrom?: number;
  'aria-label'?: string;
}

const textOf = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as any).children);
  return '';
};

function collect(children: ReactNode, group?: string, out: Opt[] = []): Opt[] {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props as any;
    if (child.type === 'option') {
      const label = textOf(props.children);
      out.push({
        value: props.value !== undefined ? String(props.value) : label,
        label,
        disabled: !!props.disabled,
        group,
      });
    } else if (child.type === 'optgroup') {
      collect(props.children, props.label, out);
    } else if (props?.children) {
      // Fragments and wrappers around options.
      collect(props.children, group, out);
    }
  });
  return out;
}

export function SelectField({
  value,
  defaultValue,
  onChange,
  children,
  className = '',
  disabled,
  name,
  id,
  title,
  searchFrom = 6,
  'aria-label': ariaLabel,
}: Props) {
  const options = useMemo(() => collect(children), [children]);
  const [inner, setInner] = useState(defaultValue != null ? String(defaultValue) : '');
  const current = value != null ? String(value) : inner;
  const selected = options.find(o => o.value === current) ?? (current === '' ? undefined : undefined);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const showSearch = options.length >= searchFrom;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.label.toLowerCase().includes(q) || (o.group ?? '').toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const i = options.findIndex(o => o.value === current);
    setActive(i >= 0 ? i : 0);
    // Focus the search box (or the list) once the panel is mounted.
    const t = setTimeout(() => (showSearch ? searchRef.current : listRef.current)?.focus(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Typing a search jumps the highlight to the first match.
  useEffect(() => { if (open && query) setActive(0); }, [query, open]);

  // Keep the highlighted row in view while arrowing.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (o: Opt) => {
    if (o.disabled) return;
    if (value == null) setInner(o.value);
    const ev: ChangeLike = { target: { value: o.value, name }, currentTarget: { value: o.value, name } };
    onChange?.(ev);
    setOpen(false);
    anchorRef.current?.focus();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const o = filtered[active]; if (o) pick(o); }
    else if (e.key === 'Tab') setOpen(false);
  };

  // The placeholder option (value "") shows muted, like a native select.
  const isPlaceholder = !selected || selected.value === '';
  const shownLabel = selected?.label ?? options.find(o => o.value === '')?.label ?? '';

  let lastGroup: string | undefined;

  return (
    <>
      <button
        ref={anchorRef}
        id={id}
        type="button"
        name={name}
        title={title}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`flex items-center justify-between gap-2 text-left ${/\bbg-/.test(className) ? '' : 'bg-white'} ${className} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <span className={`min-w-0 truncate ${isPlaceholder ? 'text-slate-400' : ''}`}>{shownLabel || ' '}</span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <Floating
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        className="min-w-[180px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
      >
        {showSearch && (
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onKey}
                placeholder="Search…"
                className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#6366F1] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/20"
              />
            </div>
          </div>
        )}
        <div ref={listRef} role="listbox" tabIndex={-1} onKeyDown={onKey} className="max-h-64 overflow-y-auto py-1 outline-none">
          {filtered.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">No matches</p>}
          {filtered.map((o, i) => {
            const header = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            const isSel = o.value === current;
            return (
              <div key={`${o.group ?? ''}|${o.value}|${i}`}>
                {header && <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{header}</p>}
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  data-idx={i}
                  disabled={o.disabled}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                    o.disabled ? 'cursor-not-allowed text-slate-300'
                      : i === active ? 'bg-indigo-50 text-slate-900'
                        : 'text-slate-700'
                  } ${o.value === '' ? 'text-slate-400' : ''}`}
                >
                  <span className="min-w-0 truncate">{o.label}</span>
                  {isSel && o.value !== '' && <Check className="h-4 w-4 flex-shrink-0 text-[#6366F1]" />}
                </button>
              </div>
            );
          })}
        </div>
      </Floating>
    </>
  );
}
